import React, { useEffect, useState } from 'react';
import { C } from '../../shared/design-tokens.js';
import Drawer from '../../shared/ui/Drawer.js';
import { apiFetch, apiWrite } from '../../lib/api-client.js';
import { vehicleStatusPresentation, leaseTypeLabel, CONTROL_INPUT, MUTED_LABEL } from './fleet-ui.js';
import type { FleetVehicle, TerminalOption } from './use-fleet-read-model.js';

interface VehicleDetailIssue {
  id: string | number;
  status?: string;
  date?: string;
  reported_date?: string;
  issue?: string;
  issue_description?: string;
}

interface VehicleDetailResponse {
  vehicle?: FleetVehicle;
  recent_issues?: VehicleDetailIssue[];
  error?: string;
}

interface FleetVehicleDetailDrawerProps {
  vehicleId: string | number | null;
  onClose: () => void;
  terminalOptions: TerminalOption[];
  onTransferComplete?: () => void;
}

export default function FleetVehicleDetailDrawer({ vehicleId, onClose, terminalOptions, onTransferComplete }: FleetVehicleDetailDrawerProps) {
  const [vehicle, setVehicle] = useState<FleetVehicle | null>(null);
  const [recentIssues, setRecentIssues] = useState<VehicleDetailIssue[]>([]);
  const [loading, setLoading] = useState(false);
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [transferTerminalId, setTransferTerminalId] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [transferSuccess, setTransferSuccess] = useState(false);
  const [resolvingIssueId, setResolvingIssueId] = useState<string | number | null>(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolveSubmitting, setResolveSubmitting] = useState(false);

  useEffect(() => {
    setShowTransferForm(false);
    setTransferTerminalId('');
    setTransferNotes('');
    setTransferError(null);
    setTransferSuccess(false);
    if (!vehicleId) { setVehicle(null); setRecentIssues([]); return; }
    setLoading(true);
    apiFetch<VehicleDetailResponse>(`/api/fleet/vehicles/${vehicleId}`)
      .then((data) => {
        setVehicle(data.vehicle || null);
        setRecentIssues(data.recent_issues || []);
      })
      .catch(() => { setVehicle(null); setRecentIssues([]); })
      .finally(() => setLoading(false));
  }, [vehicleId]);

  const status = vehicle ? vehicleStatusPresentation(vehicle.current_status || '') : null;
  const drawerTitle = vehicle ? `Vehicle #${vehicle.truck_number || '—'}` : 'Vehicle';

  return (
    <Drawer open={!!vehicleId} onClose={onClose} title={drawerTitle} width={440}>
      {loading ? (
        <div style={{ color: C.textMuted, marginTop: 40 }}>Loading vehicle...</div>
      ) : !vehicle ? (
        <div style={{ color: C.red, marginTop: 40 }}>Vehicle not found.</div>
      ) : (
        <>
          <div style={{ color: C.text, fontSize: 22, fontWeight: 700, marginBottom: 4 }}>#{vehicle.truck_number || '—'}</div>
          <div style={{ color: C.textMuted, fontSize: 12, marginBottom: 4 }}>
            {vehicle.year ? `${vehicle.year} ` : ''}{vehicle.make || ''} {vehicle.model || ''}
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 999, color: status!.color, background: status!.background }}>{status!.label}</span>
            <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 999, color: C.textMuted, background: C.panel }}>{leaseTypeLabel(vehicle.lease_type)}</span>
          </div>

          <div style={{ display: 'grid', gap: 10 }}>
            {[
              { label: 'Terminal',    value: vehicle.terminal_name || '—' },
              { label: 'VIN',         value: vehicle.vin || '—' },
              { label: 'Plate',       value: vehicle.license_plate || '—' },
              { label: 'Mileage',     value: vehicle.last_known_mileage ? `${vehicle.last_known_mileage.toLocaleString()} mi` : (vehicle.current_mileage ? `${vehicle.current_mileage.toLocaleString()} mi` : '—') },
              ...(vehicle.current_status === 'oos' ? [{ label: 'OOS reason', value: vehicle.oos_reason || 'Not specified', accent: C.red }] : []),
            ].map(({ label, value, accent }) => (
              <div key={label} style={{ padding: 10, borderRadius: 8, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
                <div style={{ color: C.textMuted, fontSize: 11, marginBottom: 3 }}>{label}</div>
                <div style={{ color: accent || C.text, fontSize: 13 }}>{value}</div>
              </div>
            ))}
          </div>

          {recentIssues.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ color: C.gold, fontSize: 13, marginBottom: 8 }}>Recent issues ({recentIssues.length})</div>
              <div style={{ display: 'grid', gap: 8 }}>
                {recentIssues.slice(0, 5).map((issue, idx) => (
                  <div key={issue.id || idx} style={{ padding: 10, borderRadius: 8, background: C.panel, border: `1px solid ${issue.status === 'open' ? 'rgba(255,107,107,0.3)' : C.borderSoft}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ color: issue.status === 'open' ? C.red : C.textMuted, fontSize: 11, textTransform: 'uppercase' }}>{issue.status || 'open'}</div>
                      <div style={{ color: C.textMuted, fontSize: 11 }}>{issue.date || issue.reported_date || ''}</div>
                    </div>
                    <div style={{ color: C.text, fontSize: 12, marginTop: 4 }}>{issue.issue || issue.issue_description || '—'}</div>
                    {issue.status === 'open' && issue.id && (
                      resolvingIssueId === issue.id ? (
                        <div style={{ marginTop: 8 }}>
                          <input type="text" value={resolveNotes} onChange={(e) => setResolveNotes(e.target.value)} placeholder="Resolution notes..." style={{ ...CONTROL_INPUT, marginBottom: 6, fontSize: 12 }} />
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button type="button" disabled={resolveSubmitting} onClick={async () => {
                              setResolveSubmitting(true);
                              const { ok } = await apiWrite<{ error?: string }>(`/api/fleet/issues/${issue.id}/resolve`, 'PUT', { resolved_by: 'manager', resolution_notes: resolveNotes || null });
                              setResolveSubmitting(false);
                              if (ok) { setResolvingIssueId(null); setResolveNotes(''); if (onTransferComplete) onTransferComplete(); }
                            }} style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: C.green, color: '#111', fontSize: 11, fontWeight: 600, cursor: resolveSubmitting ? 'not-allowed' : 'pointer' }}>
                              {resolveSubmitting ? '...' : 'Resolve'}
                            </button>
                            <button type="button" onClick={() => { setResolvingIssueId(null); setResolveNotes(''); }} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.borderSoft}`, background: 'transparent', color: C.textMuted, fontSize: 11, cursor: 'pointer' }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <button type="button" onClick={() => setResolvingIssueId(issue.id)} style={{ marginTop: 6, padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.green}`, background: 'transparent', color: C.green, fontSize: 11, cursor: 'pointer' }}>Resolve</button>
                      )
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {vehicle.current_status === 'active' && terminalOptions && terminalOptions.length > 0 && !transferSuccess && (
            <div style={{ marginTop: 20 }}>
              {!showTransferForm ? (
                <button
                  type="button"
                  onClick={() => setShowTransferForm(true)}
                  style={{ width: '100%', padding: '10px 16px', borderRadius: 8, border: `1px solid ${C.gold}`, background: 'transparent', color: C.gold, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  Transfer vehicle
                </button>
              ) : (
                <div style={{ padding: 14, borderRadius: 10, background: C.panel, border: `1px solid rgba(201,162,39,0.3)` }}>
                  <div style={{ color: C.gold, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Initiate transfer</div>

                  <label style={MUTED_LABEL}>Destination terminal</label>
                  <select
                    value={transferTerminalId}
                    onChange={(e) => setTransferTerminalId(e.target.value)}
                    style={{ ...CONTROL_INPUT, marginBottom: 10 }}
                  >
                    <option value="">Select terminal...</option>
                    {terminalOptions
                      .filter((t: TerminalOption) => t.id !== 'all' && t.id !== String(vehicle.current_terminal_id))
                      .map((t: TerminalOption) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>

                  <label style={MUTED_LABEL}>Notes (optional)</label>
                  <input
                    type="text"
                    value={transferNotes}
                    onChange={(e) => setTransferNotes(e.target.value)}
                    placeholder="Reason for transfer..."
                    style={{ ...CONTROL_INPUT, marginBottom: 12 }}
                  />

                  {transferError && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{transferError}</div>}

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      disabled={!transferTerminalId || transferSubmitting}
                      onClick={async () => {
                        setTransferSubmitting(true);
                        setTransferError(null);
                        const { ok, data } = await apiWrite<{ error?: string }>('/api/fleet/transfers', 'POST', {
                          vehicle_id: vehicle.id,
                          to_terminal_id: transferTerminalId,
                          notes: transferNotes || undefined,
                        });
                        setTransferSubmitting(false);
                        if (!ok) {
                          setTransferError(data?.error || 'Transfer failed');
                          return;
                        }
                        setTransferSuccess(true);
                        if (onTransferComplete) onTransferComplete();
                      }}
                      style={{ flex: 1, padding: '10px 16px', borderRadius: 8, border: 'none', background: C.gold, color: '#0d1117', fontSize: 13, fontWeight: 700, cursor: transferTerminalId && !transferSubmitting ? 'pointer' : 'default', opacity: !transferTerminalId || transferSubmitting ? 0.5 : 1 }}
                    >
                      {transferSubmitting ? 'Submitting...' : 'Confirm transfer'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowTransferForm(false); setTransferError(null); }}
                      style={{ padding: '10px 16px', borderRadius: 8, border: `1px solid ${C.borderSoft}`, background: 'transparent', color: C.textMuted, fontSize: 13, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {transferSuccess && (
            <div style={{ marginTop: 16, padding: 12, borderRadius: 8, background: 'rgba(78,205,196,0.1)', border: '1px solid rgba(78,205,196,0.3)', color: C.green, fontSize: 13 }}>
              Transfer initiated — vehicle is now in transit.
            </div>
          )}
        </>
      )}
    </Drawer>
  );
}
